import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from 'lucide-react';
import type { VolumePage } from '../data/volumePages';
type PageLightboxProps = {
  pages: VolumePage[];
  startIndex: number;
  onClose: () => void;
};
export function PageLightbox({
  pages,
  startIndex,
  onClose
}: PageLightboxProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const [index, setIndex] = useState(startIndex);
  const goTo = useCallback((i: number, smooth = true) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(pages.length - 1, i));
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({
      left: clamped * track.clientWidth,
      behavior: smooth && !reduce ? 'smooth' : 'auto'
    });
  }, [pages.length]);

  // Abrir directamente en la imagen tocada.
  useLayoutEffect(() => {
    goTo(startIndex, false);
  }, [goTo, startIndex]);
  useEffect(() => {
    closeRef.current?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') goTo(index + 1, false);
      if (e.key === 'ArrowLeft') goTo(index - 1, false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goTo, index, onClose]);
  const handleScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    setIndex(Math.round(track.scrollLeft / track.clientWidth));
  };
  return <motion.div role="dialog" aria-modal="true" aria-label="Volume 5 pages" className="fixed inset-0 z-[100] flex flex-col bg-ink/95" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2,
    ease: [0.23, 1, 0.32, 1]
  }} data-id="element-213" data-mp="3uwqqh8sffzlu">
      <div className="flex items-center justify-between px-4 py-3" data-id="element-214" data-mp="1136fbqe69aij">
        <p className="text-sm font-bold text-cream/80" aria-live="polite" data-id="element-215" data-mp="1ftdsprpbo3cd">
          {index + 1} / {pages.length}
        </p>
        <button ref={closeRef} type="button" onClick={onClose} aria-label="Close" className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-cream transition-colors duration-150 hover:bg-white/20 focus:outline-none focus-visible:ring-4 focus-visible:ring-ochre/60" data-id="element-216" data-mp="1ax6ihmpw85om">
          <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-217" data-mp="s39lzbgu7nh5" />
        </button>
      </div>

      <div className="relative min-h-0 flex-1" data-id="element-218" data-mp="27kbiamwe77tm">
        <div ref={trackRef} onScroll={handleScroll} className="flex h-full snap-x snap-mandatory overflow-x-auto [&::-webkit-scrollbar]:hidden" style={{
        scrollbarWidth: 'none'
      }} data-id="element-219" data-mp="27n62qefeosn1">
          {pages.map((page, i) => <div key={page.src} className="flex h-full w-full flex-none snap-center items-center justify-center px-4 pb-6" data-id="element-220" data-mp="2g2v9qiuqn41r" data-mpi={i}>
              <img src={page.src} alt={page.alt} loading={i === startIndex ? 'eager' : 'lazy'} className="max-h-full max-w-full rounded-[12px] bg-white object-contain" data-id="element-221" data-mp="263nzw5n7prje" />
            </div>)}
        </div>

        <button type="button" onClick={() => goTo(index - 1)} disabled={index === 0} aria-label="Previous page" className="absolute left-4 top-1/2 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-cream transition-colors duration-150 hover:bg-white/20 disabled:opacity-30 focus:outline-none focus-visible:ring-4 focus-visible:ring-ochre/60 lg:flex" data-id="element-222" data-mp="2k3yot34yow1x">
          <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" data-id="element-223" data-mp="1twcd4nh4zpsq" />
        </button>
        <button type="button" onClick={() => goTo(index + 1)} disabled={index === pages.length - 1} aria-label="Next page" className="absolute right-4 top-1/2 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-cream transition-colors duration-150 hover:bg-white/20 disabled:opacity-30 focus:outline-none focus-visible:ring-4 focus-visible:ring-ochre/60 lg:flex" data-id="element-224" data-mp="1oun2h8xtte56">
          <ChevronRightIcon className="h-6 w-6" aria-hidden="true" data-id="element-225" data-mp="22v64nzfm1pzf" />
        </button>
      </div>
    </motion.div>;
}